<template>
  <div class="map-container fill-height">
    <FieldMap hash remember-position>
      <div class="map-overlay">
        <div class="map-overlay-toolbar">
          <vs-button
            v-if="isMobileView"
            class="overlay-button"
            :icon="navStore.isNavVisible ? 'mdi-menu-open' : 'mdi-menu'"
            type="secondary"
            @click="navStore.toggleNav"
          />
          <vs-button
            v-if="!jobFilterStore.isShowingFilters"
            class="overlay-button"
            icon="mdi-filter-outline"
            type="secondary"
            @click="jobFilterStore.showFilterPanel"
          />
          <v-menu
            offset-y
            center
            open-on-click
            :nudge-bottom="8"
            close-on-click
            :close-on-content-click="false"
          >
            <template #activator="{ on, attrs }">
              <vs-button
                class="overlay-button"
                icon="mdi-layers-outline"
                type="secondary"
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <LayersMenu />
          </v-menu>
          <FieldViewSearch />
          <div class="spacer" />
          <vs-button
            v-if="isAdmin"
            id="new-location-button"
            data-test="new-location-button"
            icon="add"
            type="primary"
            :label="!isMobileView ? 'New location' : 'Location'"
            @click="handleNewLocationClick"
          />
        </div>
        <div class="map-overlay-bottom">
          <div class="spacer" />
          <vs-button
            id="feedback-button"
            icon="mdi-comment-quote-outline"
            type="secondary"
            label="Feedback"
            small
            @click="showFeedbackDialog = true"
          />
        </div>
      </div>
      <LocationsLayer
        layer-id="locations"
        source-id="locations"
        before="locations-modified"
        :hide-layer="hideLocationsLayer"
        :hidden-feature-ids="hiddenLocationIds"
        @click="handleLocationsClicked"
      />
      <LocationsModifiedLayer
        layer-id="locations-modified"
        source-id="locations-modified"
        :show-locations="state.modifiedLocations.filter((l) => !l.isArchived)"
        :hide-locations="state.selectedLocation?.id ? [state.selectedLocation] : []"
        :hide-layer="hideLocationsLayer"
        @click="handleLocationsClicked"
      />

      <JobsLayer
        layer-id="clusters"
        source-id="jobs"
        :hidden-location-ids="selectedLocationIds"
        :hide-layer="hideJobClustersLayer"
        @click="handleJobsClustersClicked"
      />

      <ResourcesLayer
        layer-id="resources"
        source-id="resources"
        :hidden-feature-ids="hiddenResourceIds"
        :hide-layer="hideResourcesLayer"
        @click="handleResourcesClicked"
      />

      <LocationsSelectedLayer
        layer-id="locations-selected"
        source-id="locations-selected"
        :show-location="state.selectedLocation"
      />

      <ResourcesSelectedLayer
        layer-id="resources-selected"
        source-id="resources-selected"
        :show-resource="state.selectedResource"
        :hide-layer="hideResourcesLayer"
      />

      <SingleJobLayer
        v-if="state.selectedJobNumber"
        :key="state.selectedJobNumber"
        :selected-job-number="state.selectedJobNumber"
      />

      <MultiJobsLayer
        v-if="state.selectedJobNumbers.length > 0"
        :job-ids="state.selectedJobNumbers"
      />

      <NewLocationMenu />

      <RightDrawer :is-visible="showSideSheet" :full-width="isMobileView">
        <router-view />
      </RightDrawer>

      <FeedbackDialog v-if="showFeedbackDialog" @close="showFeedbackDialog = false" />
    </FieldMap>
  </div>
</template>

<script setup lang="ts">
import { GeoJSONSource, GeoJSONFeature, MapMouseEvent } from 'mapbox-gl'
import type { Feature, GeoJsonProperties, Geometry, Point } from 'geojson'

import { useRoute, useRouter } from 'vue-router/composables'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useGetters } from 'vuex-composition-helpers'
import { groupBy } from 'lodash'

import RightDrawer from '../common/RightDrawer.vue'
import LocationsLayer from './layers/LocationsLayer.vue'
import LocationsModifiedLayer from './layers/LocationsModifiedLayer.vue'
import LocationsSelectedLayer from './layers/LocationsSelectedLayer.vue'
import FieldViewSearch from './FieldViewSearch.vue'
import FieldMap from './mapbox/FieldMap.vue'
import ResourcesLayer from './layers/ResourcesLayer.vue'
import ResourcesSelectedLayer from './layers/ResourcesSelectedLayer.vue'
import JobsLayer from './layers/JobsLayer.vue'
import SingleJobLayer from '@/components/field-view-next/layers/SingleJobLayer.vue'
import MultiJobsLayer from '@/components/field-view-next/layers/MultiJobsLayer.vue'
import FeedbackDialog from '@/components/field-view-next/feedback/FeedbackDialog.vue'
import LayersMenu from '@/components/field-view-next/LayersMenu.vue'
import NewLocationMenu from '@/components/field-view-next/layers/NewLocationMenu.vue'
import { useCreateFieldViewState } from './FieldViewState'
import { useJobFilterStore } from './JobsFilterStore'
import { useNavStore } from '@/components/nav/navStore'
import { useScreen } from '@/common/useScreen'

const route = useRoute()
const router = useRouter()

const showFeedbackDialog = ref<boolean>()

const jobFilterStore = useJobFilterStore()
const navStore = useNavStore()
const { isMobileView } = useScreen()

const { isAdmin } = useGetters('permissions', ['isAdmin'])

const showSideSheet = computed(() => {
  return route.name !== 'field-view-next'
})

const hiddenLocationIds = computed(() => {
  const hidden = state.value.modifiedLocations.map((l) => l.id) || []

  if (state.value.selectedLocation?.id) {
    hidden.push(state.value.selectedLocation?.id)
  }

  return hidden.filter((i) => i !== null) as number[]
})

const selectedLocationIds = computed(() => {
  return state.value.selectedLocation?.id ? [state.value.selectedLocation.id] : []
})

const hiddenResourceIds = computed(() => {
  return state.value.selectedResource?.id ? [state.value.selectedResource.id] : []
})

const { state } = useCreateFieldViewState()

const handleLocationsClicked = (features: GeoJSONFeature[], eventData: MapMouseEvent) => {
  eventData.preventDefault()
  if (features.length === 1) {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: features[0].id?.toString() ?? '', source: 'locations' },
    })
  } else {
    router.push({
      name: 'fvnext-picker-panel',
      params: { ids: features.map((f) => f.id).join(','), source: 'locations' },
    })
  }
}

const handleResourcesClicked = (features: GeoJSONFeature[], eventData: MapMouseEvent) => {
  eventData.preventDefault()

  if (features.length === 1) {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: features[0].id?.toString() ?? '', source: 'resources' },
    })
  } else {
    router.push({
      name: 'fvnext-picker-panel',
      params: { ids: features.map((f) => f.id).join(','), source: 'resources' },
    })
  }
}

const handleJobsClustersClicked = (features: GeoJSONFeature[], eventData: MapMouseEvent) => {
  eventData.preventDefault()
  // should always be one feature due to clustering
  const feature = features[0]
  if (!feature) return

  if (feature.properties?.cluster) {
    const { cluster_id: clusterId, point_count: pointCount } = feature.properties
    const source = eventData.target.getSource('jobs') as GeoJSONSource

    source.getClusterLeaves(
      clusterId,
      pointCount,
      0,
      (err, leaves?: Feature<Geometry, GeoJsonProperties>[] | null) => {
        if (err || !leaves) return

        const grouped = groupBy(leaves, (leaf) => leaf.properties?.LocationId)
        const moreThanOneLocation = Object.keys(grouped).length > 1

        // if a cluster contains jobs for more than one location,
        // and the zoom level has not exceeded 16, zoom into the cluster
        // to break it up.
        if (moreThanOneLocation && eventData.target.getZoom() < 16) {
          source.getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err || zoom == null) return

            eventData.target.easeTo({
              center: (feature.geometry as Point).coordinates as [number, number],
              zoom: Math.min(16, zoom),
            })
          })
        } else if (moreThanOneLocation) {
          const jobsGroupedByLocation = Object.keys(grouped).reduce(
            (prev, location) => ({
              ...prev,
              [location]: grouped[location].map((f) => f.properties?.JobNumber),
            }),
            {},
          )
          const ids = JSON.stringify(jobsGroupedByLocation)

          router.push({
            name: 'fvnext-picker-panel',
            params: { ids, source: 'jobs-cluster' },
          })
        } else {
          router.push({
            name: 'fvnext-info-panel',
            params: {
              id: Object.keys(grouped)[0],
              source: 'locations',
            },
          })
        }
      },
    )
  } else {
    router.push({
      name: 'fvnext-info-panel',
      params: { id: feature.properties?.LocationId, source: 'locations' },
    })
  }
}

const handleNewLocationClick = () => {
  router.push({
    name: 'fvnext-new-location',
  })
}

const hideLocationsLayer = computed(() => {
  if (!state.value.layerToggles.locations) {
    return true
  }
  return false
})

const hideResourcesLayer = computed(() => {
  if (
    state.value.selectedJobNumber ||
    state.value.selectedJobNumbers.length > 0 ||
    !state.value.layerToggles.resources
  ) {
    return true
  }
  return false
})

const hideJobClustersLayer = computed(() => {
  if (
    state.value.selectedJobNumber ||
    state.value.selectedJobNumbers.length > 0 ||
    !state.value.layerToggles.jobs
  ) {
    return true
  }
  return false
})

onMounted(() => {
  document.title = 'Field View - IronSight'
})

onBeforeUnmount(() => {
  document.title = 'IronSight'
})
</script>

<style scoped>
.panel-wrapper {
  position: absolute;
}
</style>

<style scoped>
.map-container {
  position: relative;
  height: 100%;
}

#map {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

#map :focus {
  outline: none;
}

.overlay-button {
  position: relative;
  height: 48px;
  width: 48px;
  /* match the search bar height */
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  box-shadow: 0px 2px 3px 1px #272c3a80;
  color: var(--text-color);
}

.overlay-button:deep(.icon) {
  color: var(--text-color);
  font-size: 20px;
}

.overlay-button:hover {
  background-color: var(--color-blue--lighter);
}

#new-location-button {
  position: relative;
  height: 48px;
  box-shadow: 0px 2px 3px 1px #272c3a80;
  overflow: clip;
}

#feedback-button {
  position: relative;
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  box-shadow: 0 2px 3px 1px #272c3a80;
  color: var(--text-color);
}
#feedback-button:deep(.icon) {
  color: var(--text-color);
  font-size: 20px;
}

.map-overlay {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
}

.map-overlay-toolbar {
  width: 100%;
  height: 100%;
  padding: 16px 24px;
  display: flex;
  flex-direction: row;
  align-items: start;
  gap: 16px;
}
.map-overlay-bottom {
  width: 100%;
  height: 100%;
  padding: 32px 56px;
  display: flex;
  flex-direction: row;
  align-items: end;
  gap: 16px;
}

.layer-btn {
  width: 92px;
  height: 112px !important;
  span {
    color: var(--color-grey) !important;
    font-family: var(--font-display) !important;
    font-weight: 600;
    font-size: 9pt !important;
  }
}
.layer-btn.active {
  background-color: inherit !important;
  span {
    color: var(--color-blue) !important;
  }
}
.layer-btn:hover {
  span {
    color: var(--color-blue) !important;
  }
}

.layer-btn:hover::before,
.layer-btn::before {
  opacity: 0 !important;
}
.layer-btn.active:hover::before,
.layer-btn.active::before {
  opacity: 0 !important;
}

.layer-btn .layer-btn-img {
  border: solid 3px transparent;
  margin: 2px;
  width: 80px;
}

.layer-btn.active .layer-btn-img {
  border: solid 3px var(--color-blue);
}

.layers-menu {
  padding: var(--space-small) var(--space-base);
  padding-bottom: 2px;
}
</style>
